import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../common/Modal';
import ConfirmDialog from '../common/ConfirmDialog';
import './ModalForms.css';
import '../common/IconButton.css';
import { createPassenger, deletePassenger, getPassengers, updatePassenger } from '../../services/clientService';
import PassengerFormModal from './PassengerFormModal';
import { formatDni } from '../../utils/formatters';
import { IconPencil, IconTrash, IconPlus } from '../common/Icons';

const PassengerModal = ({
  isOpen,
  onClose,
  client,
  onUpdated,
  mode = 'manage', // 'manage' | 'select'
  excludePassengerIds = [],
  onSelectPassenger,
  autoEditPassengerId,
}) => {
  const [passengers, setPassengers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingPassenger, setEditingPassenger] = useState(null);
  const [saving, setSaving] = useState(false);
  const selecting = mode === 'select';

  const closeForm = useCallback(() => {
    setFormOpen(false);
    setEditingPassenger(null);
  }, []);

  const loadPassengers = useCallback(async () => {
    if (!client?.id) return;
    setLoading(true);
    try {
      const data = await getPassengers(client.id);
      setPassengers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading passengers', error);
      setPassengers([]);
    } finally {
      setLoading(false);
    }
  }, [client?.id]);

  useEffect(() => {
    if (!isOpen) return;
    loadPassengers();
    closeForm();
  }, [isOpen, loadPassengers, closeForm]);

  const didAutoEditRef = useRef(false);
  useEffect(() => {
    didAutoEditRef.current = false;
  }, [isOpen, autoEditPassengerId]);

  useEffect(() => {
    if (!isOpen) return;
    if (!autoEditPassengerId) return;
    if (loading) return;
    if (didAutoEditRef.current) return;

    const target = passengers.find((p) => Number(p?.id) === Number(autoEditPassengerId));
    if (!target) return;

    setEditingPassenger(target);
    setFormOpen(true);
    didAutoEditRef.current = true;
  }, [isOpen, autoEditPassengerId, loading, passengers]);

  const primaryPassengerId = useMemo(() => {
    const match = passengers.find((p) =>
      p.dni === client?.dni
    );
    return match?.id;
  }, [passengers, client]);

  const orderedPassengers = useMemo(
    () => [...passengers].sort((a, b) => (a.id || 0) - (b.id || 0)),
    [passengers]
  );

  const visiblePassengers = useMemo(() => {
    const excluded = new Set((excludePassengerIds || []).map((x) => Number(x)));
    return orderedPassengers.filter((p) => !excluded.has(Number(p.id)));
  }, [orderedPassengers, excludePassengerIds]);

  const handleOpenCreate = () => {
    setEditingPassenger(null);
    setFormOpen(true);
  };

  const handleOpenEdit = (p) => {
    setEditingPassenger(p);
    setFormOpen(true);
  };

  const handleDelete = async (id) => {
    if (id === primaryPassengerId) return;
    setPendingDeleteId(id);
    setConfirmOpen(true);
  };

  const handleSelect = async (p) => {
    if (!p?.id) return;
    await onSelectPassenger?.(p);
  };

  const handleSavePassenger = async (data) => {
    if (!client?.id) return;
    setSaving(true);
    try {
      if (editingPassenger?.id) {
        await updatePassenger(editingPassenger.id, data);
      } else {
        await createPassenger(client.id, data);
      }
      await loadPassengers();
      onUpdated?.();
      closeForm();
    } catch (error) {
      console.error('Error saving passenger', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Pasajeros de ${client?.surname || ''}`}
      isOpen={isOpen}
      onClose={onClose}
      footer={(
        <div className="modal-footer">
          <div className="footer-left">
            <button type="button" className="btn secondary" onClick={onClose} disabled={saving}>
              Volver
            </button>
          </div>
          <div className="footer-actions">
            <button
              type="button"
              className="btn"
              onClick={handleOpenCreate}
              disabled={!client?.id || saving}
            >
              + Nuevo pasajero
            </button>
          </div>
        </div>
      )}
    >
      {loading ? (
        <p>Cargando pasajeros...</p>
      ) : (
        <>
          <div className="passenger-list">
            {visiblePassengers.length === 0 && (
              <p>{selecting ? 'No hay pasajeros disponibles para asociar.' : 'No hay pasajeros cargados.'}</p>
            )}
            {visiblePassengers.map((p) => {
              const disabled = primaryPassengerId === p.id;
              return (
                <div key={p.id} className={`passenger-item ${disabled ? 'disabled-row' : ''}`}>
                  <div className="passenger-info">
                    <strong>{p.surname}, {p.name}</strong>
                    <span>DNI: {formatDni(p.dni) || '-'}</span>
                    <span>Nacimiento: {p.birthday?.slice(0, 10)}</span>
                  </div>
                  <div className="inline-actions">
                    {!disabled && (
                      <>
                        <button
                          type="button"
                          className="icon-btn icon-btn--edit"
                          onClick={() => handleOpenEdit(p)}
                          aria-label="Editar pasajero"
                          title="Editar"
                        >
                          <IconPencil />
                        </button>
                        <button
                          type="button"
                          className="icon-btn icon-btn--delete"
                          onClick={() => handleDelete(p.id)}
                          aria-label="Eliminar pasajero"
                          title="Eliminar"
                        >
                          <IconTrash />
                        </button>
                      </>
                    )}
                    {selecting && (
                      <button
                        type="button"
                        className="icon-btn icon-btn--select"
                        onClick={() => handleSelect(p)}
                        disabled={saving}
                        aria-label="Seleccionar pasajero"
                        title="Seleccionar"
                      >
                        <IconPlus />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <PassengerFormModal
        isOpen={formOpen}
        onClose={saving ? undefined : closeForm}
        client={client}
        initialData={editingPassenger}
        onSave={handleSavePassenger}
        submitting={saving}
      />

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Eliminar pasajero"
        message="¿Está seguro que desea eliminar este pasajero?"
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        onConfirm={async () => {
          if (!pendingDeleteId) return;
          await deletePassenger(pendingDeleteId);
          setConfirmOpen(false);
          setPendingDeleteId(null);
          await loadPassengers();
          onUpdated?.();
        }}
        onCancel={() => {
          setConfirmOpen(false);
          setPendingDeleteId(null);
        }}
      />
    </Modal>
  );
};

export default PassengerModal;
